import React, { useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DOMPurify from 'dompurify';

import { ticketFaqCategories, TicketFaqItem } from './ticket-faq.data';

type TicketFaqProps = {
    onContinue: () => void;
};

const normalizeText = (value: string) =>
    value
        .replace(/ي/g, 'ی')
        .replace(/ك/g, 'ک')
        .replace(/\u200c/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

const stripHtml = (value: string) =>
    value.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ');

const allFaqItems: TicketFaqItem[] = ticketFaqCategories.flatMap(
    (category) => category.items,
);

const TicketFaq = ({ onContinue }: TicketFaqProps) => {
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState<string | false>(false);

    const normalizedSearch = normalizeText(search);

    const visibleItems = useMemo(() => {
        if (!normalizedSearch) return allFaqItems;

        return allFaqItems.filter((item) => {
            const searchable = normalizeText(
                `${item.question} ${stripHtml(item.answerHtml)}`,
            );
            return searchable.includes(normalizedSearch);
        });
    }, [normalizedSearch]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: '100%',
            }}>
            <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: 1.5 }}>
                <Box
                    sx={(theme) => ({
                        p: 1.5,
                        mb: 1.5,
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: alpha(theme.palette.primary.main, 0.12),
                        backgroundColor:
                            theme.palette.mode === 'dark'
                                ? alpha(theme.palette.primary.main, 0.07)
                                : alpha(theme.palette.primary.main, 0.045),
                    })}>
                    <Typography
                        sx={{
                            color: 'text.primary',
                            fontFamily: 'YekanBalkhHeavy',
                            fontSize: 15,
                            mb: 0.25,
                        }}>
                        پاسخ سوال خود را جستجو کنید
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', lineHeight: 1.9 }}>
                        سوال یا عبارت موردنظر را بنویسید تا پاسخ‌های مرتبط نمایش داده شوند.
                    </Typography>
                </Box>

                <TextField
                    fullWidth
                    value={search}
                    onChange={(event) => {
                        setSearch(event.target.value);
                        setExpanded(false);
                    }}
                    placeholder="جستجو در سوالات پرتکرار..."
                    autoComplete="off"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <i
                                    className="fa-light fa-magnifying-glass"
                                    style={{ fontSize: 15 }}
                                />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            minHeight: 46,
                            borderRadius: '11px',
                        },
                    }}
                />
            </Box>

            <Box
                sx={{
                    px: { xs: 2, sm: 2.5 },
                    pb: 1,
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}>
                {visibleItems.length ? (
                    visibleItems.map((item) => (
                        <Accordion
                            key={item.id}
                            disableGutters
                            expanded={expanded === item.id}
                            onChange={(_, nextExpanded) =>
                                setExpanded(nextExpanded ? item.id : false)
                            }
                            sx={(theme) => ({
                                mb: 1,
                                border: '1px solid',
                                borderColor:
                                    expanded === item.id
                                        ? alpha(theme.palette.primary.main, 0.22)
                                        : theme.palette.divider,
                                borderRadius: '12px !important',
                                overflow: 'hidden',
                                boxShadow: 'none',
                                backgroundImage: 'none',
                                backgroundColor:
                                    expanded === item.id
                                        ? alpha(
                                              theme.palette.primary.main,
                                              theme.palette.mode === 'dark'
                                                  ? 0.055
                                                  : 0.028,
                                          )
                                        : theme.palette.background.paper,
                                '&:before': { display: 'none' },
                            })}>
                            <AccordionSummary
                                expandIcon={
                                    <i
                                        className="fa-light fa-chevron-down"
                                        style={{ fontSize: 12 }}
                                    />
                                }
                                sx={{
                                    px: 1.5,
                                    minHeight: 52,
                                    '& .MuiAccordionSummary-content': {
                                        my: 1.15,
                                        minWidth: 0,
                                    },
                                }}>
                                <Typography
                                    sx={{
                                        color: 'text.primary',
                                        fontSize: 14,
                                        fontWeight: 500,
                                        lineHeight: 1.9,
                                    }}>
                                    {item.question}
                                </Typography>
                            </AccordionSummary>

                            <AccordionDetails
                                sx={{ px: 1.75, pt: 0, pb: 1.75 }}>
                                <Box
                                    sx={(theme) => ({
                                        color: theme.palette.text.secondary,
                                        fontSize: 13.5,
                                        fontWeight: 400,
                                        lineHeight: 2.2,
                                        textAlign: 'justify',
                                        overflowWrap: 'anywhere',
                                        '& p': { margin: 0 },
                                        '& ul, & ol': {
                                            margin: theme.spacing(0.75, 0),
                                            paddingInlineStart: theme.spacing(2.5),
                                        },
                                        '& li': {
                                            marginBottom: theme.spacing(0.4),
                                        },
                                        '& a': {
                                            color: theme.palette.primary.main,
                                            textDecoration: 'none',
                                        },
                                    })}
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(
                                            item.answerHtml,
                                        ),
                                    }}
                                />
                            </AccordionDetails>
                        </Accordion>
                    ))
                ) : (
                    <Box
                        sx={{
                            py: 5,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                        }}>
                        <img
                            src="/images/no-exist.png"
                            alt="no result"
                            style={{ width: 74, marginBottom: 12 }}
                        />
                        <Typography sx={{ color: 'text.primary', mb: 0.5 }}>
                            نتیجه‌ای پیدا نشد
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            عبارت دیگری را جستجو کنید یا تیکت جدید بسازید.
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box
                sx={(theme) => ({
                    flexShrink: 0,
                    px: { xs: 2, sm: 2.5 },
                    py: 1.5,
                    borderTop: '1px solid',
                    borderColor: theme.palette.divider,
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? alpha(theme.palette.background.paper, 0.96)
                            : theme.palette.background.paper,
                    display: 'flex',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1.25,
                })}>
                <Box>
                    <Typography
                        sx={{
                            color: 'text.primary',
                            fontSize: 13.5,
                            fontWeight: 500,
                        }}>
                        پاسخ سوالت را پیدا نکردی؟
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        درخواستت را برای تیم پشتیبانی ارسال کن.
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    disableElevation
                    onClick={onContinue}
                    sx={{
                        minWidth: { xs: '100%', sm: 165 },
                        minHeight: 42,
                        borderRadius: '10px',
                    }}>
                    ایجاد تیکت
                </Button>
            </Box>
        </Box>
    );
};

export default TicketFaq;
